import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMG } from '../constants/site';
import { useLanguage } from '../i18n/LanguageContext';
import { useSiteImage } from '../components/SiteImages';
import { useSEO } from '../components/SEO';
import { Reveal, MaskedTitle, Overline, EASE } from '../components/motion';
import { LeadForm } from '../components/LeadForm';
import { PanelSketch } from '../components/PanelSketch';
import { Accessories } from '../components/Accessories';
import { PvcProfiles } from '../components/PvcProfiles';
import { NzebSection } from '../components/NzebSection';
import { GarageFinishes } from '../components/GarageFinishes';
import { BftAutomation } from '../components/BftAutomation';

const CONFIG = {
  pvc: { img: '/images/chapter-pvc.jpg', gallery: [IMG.interior1, IMG.house1, IMG.house3], service: 'pvc', productsImg: '/products-pvc.webp' },
  garaj: { img: '/images/hero-garaj.jpg', gallery: [IMG.garaj2, IMG.garaj3, IMG.garaj4], service: 'garaj' },
  auto: { img: '/images/chapter-auto.jpg', gallery: ['/images/chapter-auto.jpg', IMG.auto, IMG.house4], service: 'automatizari' },
};

// Catalog panel sketch shown inside the expanded card, by product index
const GARAJ_SKETCH = { 0: 'single', 1: 'multi', 2: 'flat', 3: 'blocks', 6: 'multirib' };

export default function ServicePage({ serviceKey }) {
  const { t } = useLanguage();
  const cfg = CONFIG[serviceKey];
  const s = t(`services.${serviceKey}`);
  useSEO(serviceKey);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 500], [0, 80]);
  const heroImg = useSiteImage(`hero.${serviceKey}`, cfg.img);
  const productsImg = useSiteImage(`products.${serviceKey}`, cfg.productsImg || cfg.gallery[0]);
  const gallery = [
    useSiteImage(`gallery.${serviceKey}.0`, cfg.gallery[0]),
    useSiteImage(`gallery.${serviceKey}.1`, cfg.gallery[1]),
    useSiteImage(`gallery.${serviceKey}.2`, cfg.gallery[2]),
  ];

  return (
    <main data-testid={`service-page-${serviceKey}`}>
      <section className="relative min-h-[85svh] flex items-center overflow-hidden pt-20">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
          <motion.img
            src={heroImg}
            alt={s.title}
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        </motion.div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}>
              <Overline className="mb-8">{s.overline}</Overline>
            </motion.div>
            <MaskedTitle
              lines={[s.title]}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] mb-8 max-w-3xl text-white"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
              className="text-white/75 text-base md:text-lg max-w-2xl leading-relaxed mb-10"
            >
              {s.desc}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65, ease: EASE }}>
              <a
                href="#service-form"
                data-testid={`service-cta-${serviceKey}`}
                className="inline-flex items-center gap-3 bg-accent text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-4 hover:bg-accenth transition-colors duration-300"
              >
                {t('nav.cta')} <ArrowRight size={16} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal className="mb-14">
          <Overline className="mb-4">{t('common.benefits')}</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{s.title}</h2>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-line/10">
          {s.benefits.map((b, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div data-testid={`${serviceKey}-benefit-${i}`} className="bg-paper p-5 md:p-8 h-full hover:bg-surface transition-colors duration-500">
                <span className="font-display text-sm text-accent tracking-[0.2em]">0{i + 1}</span>
                <h3 className="font-display text-base md:text-lg font-medium mt-3 md:mt-5 mb-2 md:mb-3">{b.t}</h3>
                <p className="text-smoke text-xs md:text-sm leading-relaxed">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="product-solutions-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 space-y-10">
              <Reveal>
                <Overline className="mb-4">{s.overline}</Overline>
                <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight">{t('common.products')}</h2>
              </Reveal>
              <Reveal>
                <div className="rounded-[2rem] overflow-hidden bg-white shadow-2xl shadow-ink/10 group">
                  <img
                    src={productsImg}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover transition-transform duration-[1.2s] group-hover:scale-[1.03]"
                  />
                </div>
              </Reveal>
              <Reveal>
                <a
                  href="#service-form"
                  data-testid={`${serviceKey}-products-cta`}
                  className="group/cta inline-flex items-center gap-3 bg-accent text-white rounded-full px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] hover:gap-5 transition-[gap] duration-300"
                >
                  {t('nav.cta')} <ArrowRight size={15} />
                </a>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-col gap-14 md:gap-20">
              {s.products.map((p, i) => {
                const sketch = serviceKey === 'garaj' ? GARAJ_SKETCH[i] : undefined;
                return (
                  <Reveal key={i} delay={Math.min(i * 0.05, 0.3)}>
                    <div data-testid={`${serviceKey}-product-${i}`} className="group relative">
                      <span
                        aria-hidden="true"
                        className="pointer-events-none select-none absolute -top-9 md:-top-12 -left-1 md:-left-3 font-display text-7xl md:text-9xl font-semibold text-ink/5 leading-none"
                      >
                        {(i + 1).toString().padStart(2, '0')}
                      </span>
                      <div className="relative">
                        <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight leading-snug max-w-xl transition-[transform,color] duration-300 group-hover:translate-x-2 group-hover:text-accent">
                          {p.t}
                        </h3>
                        <p data-testid={`${serviceKey}-product-${i}-desc`} className="text-smoke text-base md:text-lg leading-relaxed mt-4 max-w-2xl">
                          {p.d}
                        </p>
                        {sketch && (
                          <div className="w-full max-w-[180px] mt-6 opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                            <PanelSketch model={sketch} />
                          </div>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {serviceKey === 'pvc' && <NzebSection />}
      {serviceKey === 'pvc' && <PvcProfiles />}
      {serviceKey === 'pvc' && <Accessories />}
      {serviceKey === 'garaj' && <GarageFinishes />}
      {serviceKey === 'auto' && <BftAutomation />}

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal className="mb-14">
          <Overline className="mb-4">{t('common.process')}</Overline>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {s.process.map((step, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`${serviceKey}-process-${i}`} className="relative pt-8 border-t border-line/15">
                <span className="absolute -top-[5px] left-0 w-2.5 h-2.5 bg-accent" />
                <span className="font-display text-5xl font-medium text-outline block mb-4">0{i + 1}</span>
                <h3 className="font-display text-lg font-medium mb-2">{step.t}</h3>
                <p className="text-smoke text-sm leading-relaxed">{step.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <Reveal className="mb-10 flex items-end justify-between gap-6">
          <Overline>{t('common.gallery')}</Overline>
          <Link to="/portofoliu" data-testid={`${serviceKey}-gallery-link`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent hover:gap-4 transition-all duration-300">
            {t('nav.portofoliu')} <ArrowRight size={14} />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {gallery.map((g, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="overflow-hidden group">
                <img src={g} alt={`${s.title} — galerie lucrări`} loading="lazy" decoding="async" className={`w-full h-64 md:h-80 object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${i % 2 ? 'clip-frame-alt' : 'clip-frame'}`} />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="service-form" className="bg-surface2 border-t border-line/10 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="mb-4">{t('nav.cta')}</Overline>
              <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tight leading-tight">{t('common.ctaTitle')}</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="bg-paper border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-10 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
              <LeadForm testIdPrefix={`${serviceKey}-lead-form`} defaultServices={[cfg.service]} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
